/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { forwardRef } from 'react'
import { Avatar, type AvatarProps } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import type { TeamHistoryOfficialView } from '@sportninja/common/features/registration/teamHistory'
import { weightSemiBold } from 'src/components/css'

type Props = Omit<AvatarProps, 'size' | 'src' | 'alt' | 'children'> & {
  official: TeamHistoryOfficialView
  /** 24 in the dropdown's avatar stack; 32 in the summary card's list. */
  size?: 24 | 32
}

/**
 * An official's photo thumbnail, or their initials when there is none.
 *
 * Forwards its ref and remaining props to the avatar so a `Tooltip` can wrap it
 * directly — a wrapper element would break `Avatar.Group`'s overlap.
 */
const OfficialAvatar = forwardRef<HTMLSpanElement, Props>(
  ({ official, size = 24, ...rest }, ref) => (
    <Avatar
      {...rest}
      ref={ref}
      size={size}
      src={getImageThumbnailId({ image: official.image }) || undefined}
      alt={official.fullName}
      css={avatarStyle}
    >
      {official.initials}
    </Avatar>
  )
)

OfficialAvatar.displayName = 'OfficialAvatar'

export default OfficialAvatar

const avatarStyle = css`
  ${weightSemiBold}
  flex-shrink: 0;
  background-color: ${colors.SECONDARY_100};
  color: ${colors.SECONDARY_50};
`
